<template>
  <ScrollArea>
    <div
      v-if="accessDenied2" >
      <h1 class="settings__title">
        Organisation quick replies
      </h1>
      <Forbidden />
    </div>

    <div
      v-else-if="!forbidden"
      class="settings--limit-width">
      <h1 class="settings__title">
        Organisation quick replies
      </h1>

      <InfoCard
        v-if="quickRepliesLoaded && !errorMessage && !quickRepliesExist"
        :flat-icon="SBIconList.QUICKREPLY_LOGO"
        title="Add quick replies"
        description="Use quick replies to respond faster to inbound messages. Quick replies are your saved responses to common questions that allow you to respond faster to inbound messages and minimize the repetitive nature of replying to frequently asked questions."
        action-text="Add quick replies"
        @action="openAddQuickReplyModal" />

      <Spinner
        v-else-if="!quickRepliesLoaded"
        class="mt-5"
        :thickness="2" />

      <SettingsNote
        v-else-if="errorMessage"
        :icon="SBIconList.ERROR"
        text="Error loading data..." />

      <template v-else>
        <!-- ACTION BAR -->
        <div
          class="sticky-top" >
          <!-- Search section -->
          <div class="flex space-between settings--box bottom-20">
            <SearchInput
              ref="searchInput"
              v-model="searchQuery"
              :no-debounce="true"
              placeholder="Search quick replies..." />

            <div id="new-btn">
              <b-button
                class="flex center-all"
                :disabled="maxNumberOfQuickRepliesReached"
                @click="openAddQuickReplyModal">
                <SBIcon
                  class="mr-2"
                  :icon="SBIconList.PLUS"
                  :color="SBColors.blackPrimary"
                  :size="8"/>
                <span>New</span>
              </b-button>
            </div>

            <b-popover
              v-if="maxNumberOfQuickRepliesReached"
              target="new-btn"
              triggers="hover"
              placement="auto"
              custom-class="basic-popover" >
              You've reached the maximum number of quick replies.
            </b-popover>
          </div>
        </div>

        <Spinner
          v-if="loadingQuickRepliesFaked"
          style="height: 65px;"
          class="mt-5 spinner-border-top"
          :thickness="2" />

        <!-- No data info -->
        <div
          v-else-if="!filteredQuickRepliesList.length"
          class="no-data flex center-all">
          <span class="wrapper flex center-all">
            <span class="emoji">😐</span>
            <p>We couldn't find anything for <strong>{{ searchQuery }}</strong></p>
          </span>
        </div>

        <!-- TABLE -->
        <div
          v-else
          ref="tableGrid"
          class="table__grid bottom-40 clickable">
          <span class="column-name sticky-top sticky-second">Name</span>
          <span class="column-name sticky-top sticky-second">Message</span>
          <span class="column-name sticky-top sticky-second">Created at</span>
          <span class="column-name sticky-top sticky-second" />

          <span
            v-if="true"
            class="full-width table-count">QUICK REPLIES ({{ filteredQuickRepliesList.length }})</span>

          <template
            v-for="(qr, index) in filteredQuickRepliesList" >
            <!-- Name column cell -->
            <div
              :key="`qr-${index}`"
              class="grid-item"
              :class="[
                `hover-${index}`,
                'flex',
                'flex-align-center',
                {
                  hide: !filteredQuickRepliesListIds.includes(qr.id),
                  'tag-error': whatsAppFormatToHTML(qr.text).indexOf('<code><b>') >= 0,
                },
              ]"
              @mouseleave="removeHover(index)"
              @mouseenter="addHover(index)"
              @click="selectRow(qr)" >
              <p
                class="ellipsis"
                :inner-html.prop="(!qr.keyword ? 'No name' : qr.keyword) | boldMatch(searchQuery)" />
            </div>

            <!-- Message column cell -->
            <div
              :key="`type-${index}`"
              class="grid-item tag-content"
              :class="[`hover-${index} flex flex-align-center`, {hide: !filteredQuickRepliesListIds.includes(qr.id)}]"
              @mouseleave="removeHover(index)"
              @mouseenter="addHover(index)"
              @click="selectRow(qr)" >
              <p
                class="ellipsis"
                :inner-html.prop="flattenHTML(whatsAppFormatToHTML(qr.text))" />
            </div>

            <!-- Created at column cell -->
            <div
              :key="`join-${index}`"
              class="grid-item"
              :class="[`hover-${index} flex flex-align-center`, {hide: !filteredQuickRepliesListIds.includes(qr.id)}]"
              @mouseleave="removeHover(index)"
              @mouseenter="addHover(index)"
              @click="selectRow(qr)" >
              <p
                v-if="qr.created_at"
                class="ellipsis">{{ qr.created_at | tableDate }}</p>
              <p
                v-else
                class="ellipsis no-table-data">No date</p>
            </div>

            <!-- Menu column cell -->
            <div
              :key="`action-${index}`"
              class="grid-item"
              :class="[`hover-${index} flex flex-align-center`, {hide: !filteredQuickRepliesListIds.includes(qr.id)}]"
              @mouseleave="removeHover(index)"
              @mouseenter="addHover(index)"
              @click="selectRow(qr)" >
              <b-dropdown
                no-caret
                right
                toggle-class="settings__table-action"
                @click.stop.prevent
                @show="menuOpen[index] = true"
                @hide="controlHide($event, index)">
                <template #button-content>
                  <span
                    class="menu-button flex center-all"
                    :class="{active: menuOpen[index]}" />
                </template>
                <b-dropdown-item @click.stop.prevent="editQuickReply(qr)" >Edit</b-dropdown-item>
                <b-dropdown-item @click.stop.prevent="deleteQuickReply(qr)" >Delete</b-dropdown-item>
              </b-dropdown>
            </div>
          </template>
        </div>
      </template>

      <QuickReplyEditorModal
        ref="quickReplyEditorModalRef" />

    </div>
  </ScrollArea>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import escapeStringRegexp from 'escape-string-regexp';

import ToasterMixin from '@/mixins/Toaster';
import Spinner from '../../common/Spinner';
import ScrollArea from '@/components/common/ScrollArea';
import NoPermissionMixin from '@/mixins/AccessPermission';
import InfoCard from '@/components/common/InfoCard';
import SettingsNote from './partials/SettingsNote';
import SearchInput from '@/components/business/widgets/SearchInput';
import fieldTypes from '@/helpers/lists/fieldTypes';
import UserPermissionsMixin from '@/mixins/UserPermissions.mixin';
import QuickReplyEditorModal from '@/components/business/chat/modals/QuickReplyEditor.modal';
import { whatsAppFormatToHTML, flattenHTML } from '@/helpers/stringHelpers';

export default {
  name: 'QuickRepliesSettings',
  components: {
    Spinner,
    ScrollArea,
    InfoCard,
    SettingsNote,
    SearchInput,
    QuickReplyEditorModal,
  },
  mixins: [
    ToasterMixin,
    NoPermissionMixin,
    UserPermissionsMixin,
  ],
  props: {},
  data() {
    return {
      errorMessage: '',
      forbidden: false,
      initialQuickRepliesRequestFinished: false,
      quickReplyList: [],
      searchQuery: '',
      menuOpen: [],
      maxNumberOfQuickReplies: 500,
      fieldTypes,
      loadingQuickRepliesFaked: false,
    };
  },
  computed: {
    ...mapGetters({
      quickRepliesLoaded: 'chat/quickRepliesLoaded',
      quickReplyListVuex: 'chat/quickReplyList',
      apiError: 'contact/apiError',
    }),
    accessDenied2() {
      return this.forbidden || !this.checkPermission('manage_organisation_settings');
    },
    organisationId() {
      return this.$route.params.organisationId;
    },
    filteredQuickRepliesList() {
      return this.quickReplyListVuex.filter(customField => (RegExp(`(${escapeStringRegexp(this.searchQuery)})`, 'i').test(customField.keyword)));
    },
    filteredQuickRepliesListIds() {
      return this.filteredQuickRepliesList.map(customField => customField.id);
    },
    maxNumberOfQuickRepliesReached() {
      return this.quickReplyListVuex.length >= this.maxNumberOfQuickReplies;
    },
    quickRepliesExist() {
      return this.quickReplyListVuex && this.quickReplyListVuex.length;
    },
  },
  watch: {
    apiError(error) {
      this.errorMessage = error.data.message;
    },
    accessDenied2() {
      if (!this.accessDenied2) {
        this.fetchCustomFields({
          organisationId: this.organisationId,
        });
      }
    },
  },
  mounted() {
    window.eventBus.$on('403', () => { this.forbidden = true; });
    this.finalizeSplashView();
  },
  destroyed() {
    this.reset();
  },
  methods: {
    ...mapActions({
      finalizeSplashView: 'organisation/finalizeSplashView',
      deleteQuickReplyAction: 'chat/deleteQuickReply',
    }),
    whatsAppFormatToHTML,
    flattenHTML,
    escapeStringRegexp,
    // TODO check if needed
    fieldType(field) {
      return this.fieldTypes.find(fieldType => fieldType.alias === field.type).name;
    },
    controlHide(event, index) {
      if (this.noHide) {
        this.noHide = false;
        event.preventDefault();
      } else {
        this.menuOpen[index] = false;
      }
    },
    removeHover(index) {
      const cells = this.$refs.tableGrid.getElementsByClassName(`hover-${index}`);

      for (let index = 0; index < cells.length; index++) {
        const cell = cells[index];
        cell.classList.remove('hover-grid');
        cell.classList.remove('last');
      }
    },
    addHover(index) {
      const cells = this.$refs.tableGrid.getElementsByClassName(`hover-${index}`);

      for (let index = 0; index < cells.length; index++) {
        const cell = cells[index];
        if (index === 0) {
          cell.classList.add('first');
        }
        cell.classList.add('hover-grid');
        if (index === cells.length - 1) {
          cell.classList.add('last');
        }
      }
    },
    deleteQuickReply(quickReply) {
      this.$confirmPluginModal.show({
        buttonVariant: 'RED',
        title: 'Delete quick reply',
        message: `Are you sure you want to delete quick reply <b>${quickReply.keyword}</b>?`,
        confirmButtonText: 'Delete',
        showUndoWarning: false,
        autoClose: 'NO',
        confirmCallback: () => {
          this.deleteQuickReplyAction(quickReply.id)
            .then((response) => {
              if (response.status === 200) {
                this.showToaster('Quick reply has been successfully deleted.', null, null, 'OK');
                this.$confirmPluginModal.close();
              }
            });
        },
      });
    },
    editQuickReply(quickReply) {
      this.$refs.quickReplyEditorModalRef.show(quickReply);
    },
    openAddQuickReplyModal() {
      this.$refs.quickReplyEditorModalRef.show();
    },
    selectRow(quickReply) {
      this.editQuickReply(quickReply);
    },
    reset() {
      this.errorMessage = '';
      this.initialQuickRepliesRequestFinished = false;
      this.searchQuery = '';
      this.menuOpen = [];
    },
  },
};
</script>

<style lang="scss" scoped>
.spinner-border-top {
  border-top: 1px solid #e6ebed;
  margin-top: 0 !important;
  padding-top: 40px;
}

.table__grid {
  grid-template-columns: minmax(50px, 200px) minmax(50px, auto) minmax(0, max-content) min-content !important;

  .full-width {
    grid-column: 1 / 5 !important;
  }

  .no-table-data {
    color: $greyPrimary;
  }

  .tag-error p::after {
    content: "";
    display: inline-block;
    width: 12px;
    height: 12px;
    margin-left: 5px;
    background-size: contain;
    background-position: center;
    background-image: url('~@/assets/img/SB-Icons/error.svg');
    background-repeat: no-repeat;
  }
}
</style>
